import {NODE_TYPES} from './constants';

/**
 * Wrap the given function in a Promise resolving somewhere in the future
 * 
 * @param {function():T | function()} fn
 * @return {Promise<T> | Promise<unknown>}
 * 
 * @template T
 */
function makeAsync(fn) {
    // just run the function with a timeout 0, so the UI gets a chance to update / output the console logging
    return new Promise((resolve, reject) => {
        setTimeout(async () => {
            try {
                const res = await fn();
                
                if(res){
                    resolve(res);
                } else {
                    resolve();   
                }
            }catch(e) {
                console.error(e)
                reject(e);
            }
        }, 0)
    });
}

/**
 * Execute a function after the giving timeout elapses; only the final invocation will actually be executed
 * @param {TimerHandler} fn Function to execute after timeout elapses
 * @param {Number} timeoutInMilliseconds Timeout in milliseconds
 * @return {function(...[*]=)}
 */
function debounce(fn, timeoutInMilliseconds) {
    let _timeoutId = undefined;

    return function() {
        clearTimeout(_timeoutId);

        _timeoutId = setTimeout(
            () => fn.apply(this, arguments),
            timeoutInMilliseconds
        );
    }
}

/**
 * Execute a function after the giving timeout elapses; only the initial and final invocation will actually be executed
 * @param {function(): Promise} fn Function to execute
 * @return {function(): Promise}
 */
function debounceWithLeadingCallAsync(fn){
    let _isExecuting = false;
    let _nextExecutionScheduled = false;
    let _nextExecutionArguments = undefined;

    return async function execute(){
        //Schedule a single new execution for when we are done
        if(_isExecuting){
            _nextExecutionScheduled = true;
            _nextExecutionArguments = arguments;
            return;
        }

        //Prevent further invocations
        _isExecuting = true;

        try {
            await fn.apply(this, arguments);
        } finally {
            _isExecuting = false;
        }

        //Invoke self a second time if scheduled
        if(_nextExecutionScheduled){
            const args = _nextExecutionArguments;
            
            _nextExecutionArguments = undefined;
            _nextExecutionScheduled = false;

            await execute.apply(this, args);
        }
    }
}

/**
 * Wrap a value in a proxy inspired by Vue 3 'ref's
 * @param {TType} value Value to wrap in proxy
 * @return {JourneyBuilder.Sankey.ValueRef<TType>} Reactive ref like
 * 
 * @template TType
 */
function createValueRef(value){
    let _value = value;
    let _watches = [];
    
    return {
        set value(newValue){
            if(_value === newValue){
                return newValue;
            }
            
            const oldValue = _value;
            _value = newValue;

            _watches.forEach(watch => watch(newValue, oldValue));
        },
        get value(){
            return _value;
        },

        setValueDirect(newValue){
            _value = newValue;
        },
        subscribe: function(fn){
            _watches.push(fn);
        },
        unsubscribe: function(fn){
            if(!fn){
                _watches = [];
            } else {
                const idx = _watches.findIndex(x => x === fn);
                if(idx !== -1){
                    _watches.splice(idx, 1);
                }
            }
        }
    }
}

function wrapAsModificationAwareProxy(source, onModification){
    source = source || [];

    //Support just push/slice for the time being, all invocations assumed to be our 'source' array
    const modificationFunctionWrappers = {
        push: function() {
            source.push(...arguments);
            onModification();
        },
        splice: function() {
            source.splice(...arguments);
            onModification();
        }
    }

    return new Proxy(source, {
        get(target, property) {            
            return modificationFunctionWrappers[property] || target[property];
        }
    });
}

/**
 * Certain node types don't support statistics (yet)
 * this function serves as a central point informing callers whether a node supports statistics or not
 * @param {JourneyBuilder.Sankey.SankeyNode} node
 * @return {Boolean} bit indicating statistics support for node
 */
function supportsStatistics(node){
    //Audience nodes don't have statistics (yet), and thus won't be highlighted on click
    return node.type !== NODE_TYPES.AUDIENCE;
}

export {
    makeAsync,
    debounce,
    debounceWithLeadingCallAsync,
    createValueRef,
    wrapAsModificationAwareProxy,
    supportsStatistics
}